/**
 * Pure resolver for the chat composer's `/`, `$`, and `@` trigger menus.
 *
 * Lives in its own module (not inside `chat-composer-editor.tsx`) so it
 * stays unit-testable without spinning up Tiptap, and so React Fast Refresh
 * is happy with a component-file-only export rule.
 *
 * Issue #984 — the resolver used to open menus for ANY trigger character at
 * a word boundary, which made natural inputs like `/usr/local/bin/python`,
 * `$100`, and `user@example.com` block the send key. The suppression rules
 * below are what the unit tests in `test/chat-composer-slash-state.unit.test.ts`
 * pin.
 *
 * @module components/chat/input/chat-composer-slash-state
 */

import type { ChatComposerSlashState } from "./chat-composer-editor";

/**
 * Closed-menu return value. Exported so both the editor and parent components
 * can reset to the same baseline.
 */
export const CLOSED_STATE: ChatComposerSlashState = {
  open: false,
  trigger: "slash",
  mode: "root",
  query: "",
};

/**
 * Known root-mode slash command stems. The composer suppresses the slash menu
 * for queries that aren't a prefix of any of these — so plain text like
 * `/usr/local/bin/python` stops opening a useless empty menu.
 *
 * Keep this list in sync with `SLASH_COMMAND_OPTIONS` in `slash-command-data.ts`
 * plus the explicit `/comment` handler below.
 */
const KNOWN_SLASH_STEMS = ["compact", "plan", "agent", "comment"] as const;

/**
 * Pure decision function for the slash/skill/mention menu state.
 *
 * Suppresses the menu when the surrounding text clearly isn't a command:
 * - `$100` (currency, not skill) — `$` followed by a digit
 * - `user@example.com` (email-like) — `@` followed by something containing `.`
 * - `/usr/local/bin` (file path) — `/` followed by a query that contains `/`
 * - `/random` (non-command prose) — `/` followed by a query that doesn't
 *   prefix-match any known stem
 *
 * @param textBefore - Plain text from the document start to the caret, with
 *   newlines flattened to spaces (matches what Tiptap's `textBetween(0, from,
 *   "\n", " ")` produces).
 * @param caretOffset - Caret position used to compute the trigger's range.
 *   `range.from = caretOffset - matchedText.length`, `range.to = caretOffset`.
 */
export function computeSlashStateFromText(
  textBefore: string,
  caretOffset: number,
): ChatComposerSlashState {
  // `$`-skill trigger. Suppress when it looks like a price (`$100`, `$1.99`).
  const skillMatch = textBefore.match(/(?:^|\s)(\$[^\s\n]*)$/);
  if (skillMatch) {
    const skillText = skillMatch[1];
    // If the very next character after `$` is a digit, treat as currency, not
    // a skill name. Fall through to the next trigger.
    if (!/^\$\d/.test(skillText)) {
      const rangeFrom = caretOffset - skillText.length;
      return {
        open: true,
        trigger: "skill",
        mode: "skills",
        query: skillText.slice(1).trim(),
        range: { from: rangeFrom, to: caretOffset },
      };
    }
  }

  // `@`-mention trigger. The regex already requires the `@` to sit at line
  // start or after whitespace, so `user@example.com` mid-sentence cannot
  // match — `r@example.com` doesn't satisfy the boundary. But `@example.com`
  // at the start of an input WOULD match; suppress it as an address by
  // looking for a `.` in the query.
  const mentionMatch = textBefore.match(/(?:^|\s)(@[^\s\n]*)$/);
  if (mentionMatch) {
    const mentionText = mentionMatch[1];
    const mentionQuery = mentionText.slice(1);
    const looksLikeAddress = mentionQuery.includes(".");
    if (!looksLikeAddress) {
      const rangeFrom = caretOffset - mentionText.length;
      return {
        open: true,
        trigger: "mention",
        mode: "root",
        query: mentionQuery,
        range: { from: rangeFrom, to: caretOffset },
      };
    }
  }

  // `/`-command trigger.
  const slashMatch = textBefore.match(/(?:^|\s)(\/[^\n]*)$/);
  if (!slashMatch) {
    return CLOSED_STATE;
  }

  const slashText = slashMatch[1];
  const slashQuery = slashText.slice(1);
  const rangeFrom = caretOffset - slashText.length;

  // A second `/` inside the query means this is a path (`/usr/local/bin`),
  // not a slash command. Suppress.
  if (slashQuery.includes("/")) {
    return CLOSED_STATE;
  }

  // Explicit branches for known multi-step commands. Both keep their
  // original semantics — the query after the stem feeds the submenu's filter.
  if (slashText.toLowerCase().startsWith("/comment")) {
    return {
      open: true,
      trigger: "slash",
      mode: "comments",
      query: slashText.slice("/comment".length).trim(),
      range: { from: rangeFrom, to: caretOffset },
    };
  }

  if (slashText.toLowerCase().startsWith("/agent")) {
    return {
      open: true,
      trigger: "slash",
      mode: "agents",
      query: slashText.slice("/agent".length).trim(),
      range: { from: rangeFrom, to: caretOffset },
    };
  }

  // For the root menu, only open while the user is plausibly typing a known
  // command stem. An empty query (`/`) opens the full root palette; a query
  // that prefix-matches any known stem keeps it open while they type.
  // Everything else is prose and should not interrupt the user.
  const normalizedQuery = slashQuery.toLowerCase().trim();
  if (normalizedQuery.length > 0) {
    const matchesKnownStem = KNOWN_SLASH_STEMS.some((stem) =>
      stem.startsWith(normalizedQuery),
    );
    if (!matchesKnownStem) {
      return CLOSED_STATE;
    }
  }

  return {
    open: true,
    trigger: "slash",
    mode: "root",
    query: slashQuery.trim(),
    range: { from: rangeFrom, to: caretOffset },
  };
}
